<template>
  <b-modal
    size="lg"
    hide-footer
    title="Preview Recipients"
    :visible="value"
    @change="$emit('input', $event)"
  >
    <div class="row">
      <div class="col-md-3 form-group">
        <label class="d-block form-label text-left">Topic:</label>
        <v-select
          v-model="form.ntopId"
          :options="topicOptions"
          label="text"
          :reduce="(option) => option.value"
          :clearable="false"
          placeholder="Choose a topic"
          class="searchable-select-sm"
        />
      </div>
      <div class="col-md-3 form-group">
        <label class="d-block form-label text-left">Asset:</label>
        <v-select
          v-model="form.massId"
          :options="assetOptions"
          :filterable="false"
          label="text"
          :reduce="(option) => option.value"
          clearable
          placeholder="All assets"
          class="searchable-select-sm"
          @search="onAssetSearch"
          @input="onAssetInput"
        />
      </div>
      <div class="col-md-3 form-group">
        <label class="d-block form-label text-left">Phase:</label>
        <b-form-select v-model="form.phase" :options="phaseOptions" size="sm" />
      </div>
      <div class="col-md-3 form-group">
        <label class="d-block form-label text-left">Severity:</label>
        <b-form-select v-model="form.severity" :options="severityOptions" size="sm" />
      </div>
    </div>
    <div class="text-right mb-3">
      <b-button size="sm" variant="primary" :disabled="!form.ntopId || running" @click="runPreview">
        <i v-if="running" class="ri-loader-line"></i>
        Run preview
      </b-button>
    </div>

    <div v-if="!hasResult && !running && !error" class="text-muted text-center py-4">
      Choose a topic and run the preview.
    </div>
    <div v-if="running" class="text-center py-4">
      <b-spinner variant="primary" size="sm" />
    </div>
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-if="hasResult && !running">
      <div v-if="isEmpty" class="text-muted text-center py-4">
        No recipient matches this item.
      </div>
      <template v-else>
        <div class="small text-muted mb-3">
          Topic: <strong>{{ itemField('topic') }}</strong>
          &middot; Category: <strong>{{ itemField('category') }}</strong>
          &middot; Company: <strong>{{ companyLabel(itemField('compId')) }}</strong>
          &middot; Asset: <strong>{{ assetLabel(itemField('massId')) }}</strong>
          &middot; Phase: <strong>{{ itemField('phase') }}</strong>
          &middot; Severity:
          <b-badge :variant="severityVariant(result.item && result.item.severity)">
            {{ itemField('severity') }}
          </b-badge>
        </div>
        <div class="row">
          <div class="col-md-6">
            <h6>Will receive ({{ result.recipients.length }})</h6>
            <div v-if="!result.recipients.length" class="text-muted small">None.</div>
            <ul v-else class="list-unstyled">
              <li v-for="(row, idx) in result.recipients" :key="idx" class="mb-1">
                <i :class="channelIcon(row.channel)"></i>
                {{ audienceLabel(row) }} &mdash; {{ row.channel }}
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <h6>Skipped ({{ result.skipped.length }})</h6>
            <div v-if="!result.skipped.length" class="text-muted small">None.</div>
            <ul v-else class="list-unstyled">
              <li v-for="(row, idx) in result.skipped" :key="idx" class="mb-1">
                <i :class="channelIcon(row.channel)"></i>
                {{ audienceLabel(row) }} &mdash; {{ row.channel }}
                <span class="text-muted">({{ row.reason }})</span>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </template>
  </b-modal>
</template>

<script>
import NotificationPreviewSvc from '@/services/NotificationPreviewSvc';
import { createMasterCrud, searchSourceOptions } from '@/services/masterCrud';
import { buildMasterParams, SEARCH_DEBOUNCE_MS } from '@/util/master/masterQuery';
import { mergeSelectedOption } from '@/util/master/searchOptions';
import { isCancelled, bodyStatusError } from '@/util/master/requestError';
import { findCompany } from '@/util/companies';
import {
  PHASE_OPTIONS, SEVERITY_OPTIONS, ASSET_SOURCE, topicCodeById, previewParams, previewErrorMessage,
  channelIcon, severityVariant,
} from '@/util/notificationConfig';

export default {
  name: 'NotificationPreviewModal',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        ntopId: '', massId: '', phase: '', severity: '',
      },
      topics: [],
      assetSourceOptions: [],
      assetSelected: null,
      contacts: [],
      optionsLoaded: false,
      running: false,
      error: '',
      result: null,
      loadSeq: 0,
      searchSeq: {},
      assetSearchTimer: null,
    };
  },
  computed: {
    topicOptions() {
      return this.topics.map((row) => ({ value: row.ntopId, text: row.ntopName }));
    },
    assetOptions() {
      return mergeSelectedOption(this.assetSourceOptions, this.assetSelected);
    },
    phaseOptions() {
      return [{ value: '', text: 'Any phase' }].concat(PHASE_OPTIONS);
    },
    severityOptions() {
      return [{ value: '', text: 'Any severity' }].concat(SEVERITY_OPTIONS);
    },
    hasResult() {
      return !!this.result;
    },
    isEmpty() {
      return this.hasResult && !this.result.recipients.length && !this.result.skipped.length;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.loadOptions();
        this.loadAssetOptions('');
      } else {
        clearTimeout(this.assetSearchTimer);
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.assetSearchTimer);
  },
  methods: {
    channelIcon,
    severityVariant,
    itemField(key) {
      const item = this.result && this.result.item;
      const raw = item ? item[key] : undefined;
      return raw === undefined || raw === null || raw === '' ? '-' : raw;
    },
    // compId/massId 0 artinya gak dibatasi
    companyLabel(value) {
      if (Number(value) === 0) return 'All companies';
      const companies = this.$store.getters['auth/availableCompany'];
      const hit = findCompany(companies, value);
      return hit && hit.compName ? hit.compName : value;
    },
    assetLabel(value) {
      if (Number(value) === 0) return 'All assets';
      if (this.assetSelected && String(this.assetSelected.value) === String(value)) {
        return this.assetSelected.text;
      }
      const hit = this.assetSourceOptions.find((option) => String(option.value) === String(value));
      return hit ? hit.text : value;
    },
    // debounce + nomor urut biar respons pencarian yang basi diabaikan
    onAssetSearch(query, setLoading) {
      clearTimeout(this.assetSearchTimer);
      setLoading(true);
      this.assetSearchTimer = setTimeout(async () => {
        await this.loadAssetOptions(query);
        setLoading(false);
      }, SEARCH_DEBOUNCE_MS);
    },
    async loadAssetOptions(query) {
      this.searchSeq.massId = (this.searchSeq.massId || 0) + 1;
      const seq = this.searchSeq.massId;
      try {
        const options = await searchSourceOptions(ASSET_SOURCE, { query, max: 50 });
        if (seq === this.searchSeq.massId) this.assetSourceOptions = options;
      } catch (e) {
        if (seq === this.searchSeq.massId) this.assetSourceOptions = [];
      }
    },
    onAssetInput(value) {
      if (value === null || value === undefined || value === '') {
        this.assetSelected = null;
        return;
      }
      const hit = this.assetSourceOptions.find((option) => String(option.value) === String(value));
      this.assetSelected = hit || { value, text: String(value) };
    },
    // MEMBERS gak punya id, jadi jangan dicetak
    audienceLabel(row) {
      const name = this.contactName(row);
      return name ? `${row.audienceType} ${name}` : row.audienceType;
    },
    contactName(row) {
      if (row.audienceType !== 'CONTACT') return row.id === '' || row.id === null ? '' : row.id;
      const hit = this.contacts.find((contact) => String(contact.ntcnId) === String(row.id));
      return hit ? hit.ntcnName : `#${row.id}`;
    },
    async loadOptions() {
      if (this.optionsLoaded) return;
      try {
        const [topicBody, contactBody] = await Promise.all([
          createMasterCrud('notification/topic').list(buildMasterParams({ page: 1, max: 1000 })),
          createMasterCrud('notification/contact').list(buildMasterParams({ page: 1, max: 1000 })),
        ]);
        this.topics = Array.isArray(topicBody.data) ? topicBody.data : [];
        this.contacts = Array.isArray(contactBody.data) ? contactBody.data : [];
        this.optionsLoaded = true;
      } catch (e) {
        if (!isCancelled(e)) console.warn('preview options failed:', e?.message);
      }
    },
    async runPreview() {
      const ntopCode = topicCodeById(this.topics, this.form.ntopId);
      if (!ntopCode) return;
      const params = previewParams({
        ntopCode,
        massId: this.form.massId,
        phase: this.form.phase,
        severity: this.form.severity,
      });
      this.loadSeq += 1;
      const seq = this.loadSeq;
      this.running = true;
      this.error = '';
      try {
        const { data: body } = await NotificationPreviewSvc.getPreview(params);
        if (seq !== this.loadSeq) return;
        const failure = bodyStatusError(body);
        if (failure) {
          this.result = null;
          this.error = previewErrorMessage(failure.errorCode, failure.message);
        } else {
          this.result = body;
        }
      } catch (e) {
        if (seq !== this.loadSeq || isCancelled(e)) return;
        this.result = null;
        // 404/412 bisa datang sebagai HTTP status asli, bukan cuma body.status.errorCode
        const httpStatus = e?.response?.status;
        if (httpStatus === 404 || httpStatus === 412) {
          this.error = previewErrorMessage(httpStatus, e?.response?.data?.status?.message);
        } else {
          console.warn('preview request failed:', e?.message);
          this.error = 'Preview failed.';
        }
      } finally {
        if (seq === this.loadSeq) this.running = false;
      }
    },
  },
};
</script>

<style scoped src="../master/searchableSelectSm.css"></style>
